import { Box, Divider, IconButton, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import styled from "@emotion/styled";
import { shades } from "../../theme";
import {
  decreaseCount,
  increaseCount,
  removeFromCart,
  setIsCartOpen,
} from "../../state";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import { API_URL } from "../../apis/api";

const FlexBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CartMenu = ({ useFallback = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.cart);
  const isCartOpen = useSelector((state) => state.cart.isCartOpen);

  const totalPrice = cart.reduce((total, item) => {
    return total + item.count * item.price;
  }, 0);

  const BLOB_BASE_URL =
    "https://eezvqt4vd5kslelz.public.blob.vercel-storage.com";
  const getImageUrl = (imageName) => `${BLOB_BASE_URL}/${imageName}`;
  const PLACEHOLDER_IMAGE = getImageUrl("IMG_7816.PNG");

  return (
    <Box
      display={isCartOpen ? "block" : "none"}
      backgroundColor="rgba(0, 0, 0, 0.4)"
      position="fixed"
      zIndex={10}
      width="100%"
      height="100%"
      left="0"
      top="0"
      overflow="auto"
    >
      <Box
        position="fixed"
        right="0"
        bottom="0"
        top="60px"
        width="max(400px, 30%)"
        height="calc(100% - 60px)"
        backgroundColor="white"
        borderTopLeftRadius="22px"
        borderTopRightRadius="22px"
        boxShadow="-4px 0 24px 0 rgba(0,0,0,0.10), -1px 0 8px 0 rgba(0,0,0,0.06)"
        borderLeft="1.5px solid #ecebeb"
      >
        <Box padding="38px 30px 30px 30px" overflow="auto" height="100%">
          {/* HEADER */}
          <FlexBox mb="15px">
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: "1.35rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily:
                  "MSCHFSansMono, monospace, Arial, Helvetica, sans-serif",
              }}
            >
              SHOPPING BAG ({cart.length})
            </Typography>
            <IconButton onClick={() => dispatch(setIsCartOpen({}))}>
              <CloseIcon />
            </IconButton>
          </FlexBox>

          {/* CART LIST */}
          <Box>
            {cart.map((item) => (
              <Box key={`${item.Name}-${item.id}`}>
                <FlexBox p="15px 0">
                  <Box
                    flex="1 1 40%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 160,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "transparent",
                        borderRadius: 0,
                        boxShadow: "none",
                        overflow: "visible",
                        p: 0,
                      }}
                    >
                      <img
                        alt={item.Name}
                        src={(() => {
                          if (useFallback) {
                            // fallback mode: always use blob
                            if (item.image?.url) {
                              const filename = item.image.url.split("/").pop();
                              return getImageUrl(filename);
                            } else {
                              return PLACEHOLDER_IMAGE;
                            }
                          } else {
                            // backend mode: always use backend logic
                            if (item.image?.formats?.medium?.url) {
                              return `${API_URL}${item.image.formats.medium.url}`;
                            } else if (item.image?.url) {
                              if (item.image.url.startsWith("/")) {
                                return `${API_URL}${item.image.url}`;
                              } else {
                                return item.image.url;
                              }
                            } else {
                              return PLACEHOLDER_IMAGE;
                            }
                          }
                        })()}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          maxWidth: "90px",
                          maxHeight: "120px",
                          objectFit: "contain",
                          display: "block",
                          borderRadius: "8px",
                          minHeight: 120,
                          minWidth: 90,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box flex="1 1 60%">
                    <FlexBox mb="5px">
                      <Typography fontWeight="bold">{item.Name}</Typography>
                      <IconButton
                        onClick={() =>
                          dispatch(removeFromCart({ id: item.id }))
                        }
                      >
                        <CloseIcon />
                      </IconButton>
                    </FlexBox>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: shades.neutral[700],
                        mt: "2px",
                      }}
                    >
                      {Array.isArray(item.shortDescription)
                        ? item.shortDescription.map((block, i) =>
                            block.children?.map((child, j) => (
                              <span key={j}>{child.text}</span>
                            ))
                          )
                        : item.shortDescription}
                    </Typography>
                    <FlexBox m="15px 0">
                      <Box
                        display="flex"
                        alignItems="center"
                        border={`1.5px solid ${shades.neutral[500]}`}
                      >
                        <IconButton
                          onClick={() =>
                            dispatch(decreaseCount({ id: item.id }))
                          }
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography>{item.count}</Typography>
                        <IconButton
                          onClick={() =>
                            dispatch(increaseCount({ id: item.id }))
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <Typography fontWeight="bold">${item.price}</Typography>
                    </FlexBox>
                  </Box>
                </FlexBox>
                <Divider />
              </Box>
            ))}
          </Box>

          {/* ACTIONS */}
          <Box m="20px 0">
            <FlexBox m="20px 0">
              <Typography fontWeight="bold">SUBTOTAL</Typography>
              <Typography fontWeight="bold">${totalPrice}</Typography>
            </FlexBox>
            <CustomButton
              onClick={() => {
                navigate("/checkout");
                dispatch(setIsCartOpen({}));
              }}
            >
              CHECKOUT
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CartMenu;
